<template>
  <v-card outlined class="mt-2" :loading="isLoading" :disabled="!isManageable">
    <v-card-title class="subtitle-1 font-weight-medium primary--text py-2">
      <v-icon left color="primary">mdi-text-box-search</v-icon>
      Textos clave de la línea
    </v-card-title>

    <v-card-subtitle class="pt-0 pb-1">
      <template v-if="!isEnabled">Activa la redirección por texto clave para administrar los textos.</template>
      <template v-else-if="!whatsappId">Guarda la línea para administrar los textos clave.</template>
      <template v-else
        >Si un mensaje nuevo coincide exactamente con un texto clave, el ticket se redirige a la cola y agente
        definidos.</template
      >
    </v-card-subtitle>

    <v-card-text>
      <v-row align="center" dense>
        <v-col cols="12" sm="7">
          <v-text-field
            v-model="search"
            label="Buscar texto"
            prepend-inner-icon="mdi-magnify"
            rounded
            filled
            dense
            clearable
            :disabled="!isManageable || isLoading"
            @keyup.enter="applySearch"
            @click:clear="applySearch"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="5" class="text-right">
          <v-btn color="success" rounded :disabled="!isManageable" @click="openFormDialog">
            <v-icon left>mdi-plus</v-icon>
            Añadir texto clave
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        dense
        :headers="headers"
        :items="items"
        item-key="id"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{
          'items-per-page-options': [10, 30, 50, 100],
        }"
        no-data-text="Sin textos clave configurados"
      >
        <template v-slot:[`item.queue`]="{ item }">
          <span v-if="item.queue">{{ item.queue.name }}</span>
          <span v-else class="font-italic grey--text">Cola por defecto de la línea</span>
        </template>

        <template v-slot:[`item.user`]="{ item }">
          <span v-if="item.user">{{ item.user.name }}</span>
          <span v-else class="font-italic grey--text">Sin agente (queda pendiente)</span>
        </template>

        <template v-slot:[`item.action`]="{ item }">
          <v-tooltip top color="blue-grey">
            <template v-slot:activator="{ on }">
              <v-hover v-slot:default="{ hover }">
                <v-btn text icon v-on="on" :color="hover ? 'accent' : 'blue-grey'" @click.stop="handleEdit(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </v-hover>
            </template>
            <span>Editar texto clave</span>
          </v-tooltip>

          <v-tooltip top color="blue-grey">
            <template v-slot:activator="{ on }">
              <v-hover v-slot:default="{ hover }">
                <v-btn text icon v-on="on" :color="hover ? 'error' : 'blue-grey'" @click.stop="handleDelete(item)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-hover>
            </template>
            <span>Eliminar texto clave</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>

    <formDialog
      v-if="isOpenForm"
      :isActive="isOpenForm"
      :isEditMode="isEditMode"
      :dataToEdit="dataToEdit"
      :whatsappId="whatsappId"
      :whatsappLabel="whatsappLabel"
      @close:dialog="closeFormDialog"
    ></formDialog>
  </v-card>
</template>

<script>
import { PbxHttpClient } from "@/services/httpclient";
import { extractApiError } from "@/utils";
import formDialog from "./formDialog.vue";

export default {
  components: {
    formDialog,
  },

  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    whatsappId: {
      type: [Number, String],
      default: null,
    },
    whatsappLabel: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    isLoading: false,
    isOpenForm: false,
    isEditMode: false,
    search: "",
    // For pagination
    totalItems: 0,
    options: {
      page: 1,
      itemsPerPage: 10,
    },
    // End for pagination
    items: [],
    dataToEdit: {},
    headers: [
      {
        text: "Texto",
        align: "left",
        value: "message",
        class: "primary--text",
        width: "32%",
        sortable: false,
      },
      {
        text: "Cola destino",
        align: "left",
        value: "queue",
        class: "primary--text",
        width: "27%",
        sortable: false,
      },
      {
        text: "Agente destino",
        align: "left",
        value: "user",
        class: "primary--text",
        width: "27%",
        sortable: false,
      },
      {
        text: "Acciones",
        value: "action",
        class: "primary--text",
        width: "14%",
        align: "center",
        sortable: false,
      },
    ],
  }),

  computed: {
    isManageable() {
      return this.enabled && this.whatsappId !== null && this.whatsappId !== undefined;
    },
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },

    isManageable(value) {
      if (value) {
        this.options.page = 1;
        this.getItems();
      } else {
        this.isOpenForm = false;
        this.items = [];
        this.totalItems = 0;
      }
    },
  },

  created() {
    if (this.isManageable) this.getItems();
  },

  methods: {
    openFormDialog() {
      this.isOpenForm = true;
    },

    closeFormDialog(data = false) {
      if (data) this.getItems();

      this.isOpenForm = false;
      this.isEditMode = false;
      this.dataToEdit = {};
    },

    handleEdit(item) {
      this.isEditMode = true;
      this.dataToEdit = item;
      this.openFormDialog();
    },

    applySearch() {
      if (!this.isManageable) return;

      if (this.options.page === 1) {
        this.getItems();
      } else {
        // El watcher de options se encarga de consultar al cambiar la página.
        this.options.page = 1;
      }
    },

    async handleDelete(item) {
      try {
        const confirmRequest = await this.$swalconfirm(
          "Confirmar",
          `Está seguro de eliminar el texto clave "${item.message}"? Ésta acción no es reversible.`,
        );
        if (confirmRequest.isConfirmed) {
          await PbxHttpClient.delete(`/whatsapps/keyword-redirections/${item.id}`);
          this.$snotify.success("Texto clave eliminado", "¡Hecho!");
          this.getItems();
        }
      } catch (err) {
        this.$snotify.error(extractApiError(err, "No se pudo eliminar el texto clave."), "¡Error!");
      }
    },

    async getItems() {
      if (!this.isManageable) return;

      this.isLoading = true;

      const { page, itemsPerPage } = this.options;

      await PbxHttpClient.get("/whatsapps/keyword-redirections", {
        params: {
          whatsapp_id: this.whatsappId,
          message: this.search || undefined,
          page,
          itemPerPage: itemsPerPage,
        },
      })
        .then((res) => {
          this.items = res.data.data;
          this.totalItems = res.data.total;
        })
        .catch((err) => {
          this.$snotify.error(extractApiError(err, "Hubo un error en la consulta."), "Error");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
